/**
 * StrategyStep — "Зачем вы здесь?" fake-agency экран.
 *
 * 3 карточки: Пассив / Рост / Сеть партнёров.
 * Все три ведут дальше к одному и тому же калькулятору,
 * но иллюзия выбора создаёт вовлечённость (Pokemon GO, Genshin pattern).
 *
 * Выбор сохраняется на бэке (для аналитики и персонализации копирайта).
 */
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { SPRING_CARD, EASE_SCREEN, STAGGER_CHILDREN } from '../springs';
import { useSounds } from '../useSounds';

type Strategy = 'passive' | 'growth' | 'network';

interface StrategyStepProps {
  current: Strategy | null;
  onChoose: (s: Strategy) => void;
}

const OPTIONS: Array<{
  key: Strategy;
  icon: string;
  titleKey: string;
  subKey: string;
}> = [
  {
    key: 'passive',
    icon: '/onboarding/strategy_passive.webp',
    titleKey: 'onboarding.strategy.passiveTitle',
    subKey: 'onboarding.strategy.passiveSub',
  },
  {
    key: 'growth',
    icon: '/onboarding/strategy_growth.webp',
    titleKey: 'onboarding.strategy.growthTitle',
    subKey: 'onboarding.strategy.growthSub',
  },
  {
    key: 'network',
    icon: '/onboarding/strategy_network.webp',
    titleKey: 'onboarding.strategy.networkTitle',
    subKey: 'onboarding.strategy.networkSub',
  },
];

export function StrategyStep({ current, onChoose }: StrategyStepProps) {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const sounds = useSounds();

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center px-5 py-12 gap-10">
      <div className="text-center max-w-[320px]">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 0.9, y: 0 }}
          transition={EASE_SCREEN}
          className="font-mono"
          style={{
            fontSize: 10,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'rgba(220,230,255,0.6)',
          }}
        >
          {t('onboarding.strategy.kicker')}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...EASE_SCREEN, delay: 0.1 }}
          className="mt-3 text-[24px] leading-tight font-bold"
          style={{
            color: 'rgba(245,250,255,0.98)',
            letterSpacing: '-0.02em',
          }}
        >
          {t('onboarding.strategy.title')}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ ...EASE_SCREEN, delay: 0.25 }}
          className="mt-2 text-[13px]"
          style={{ color: 'rgba(200,210,230,0.72)' }}
        >
          {t('onboarding.strategy.subtitle')}
        </motion.p>
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: STAGGER_CHILDREN,
              delayChildren: 0.35,
            },
          },
        }}
        className="w-full max-w-[360px] flex flex-col gap-3"
      >
        {OPTIONS.map((opt) => {
          const active = current === opt.key;
          return (
            <motion.button
              key={opt.key}
              type="button"
              variants={{
                hidden: { opacity: 0, y: 18, scale: 0.97 },
                visible: { opacity: 1, y: 0, scale: 1 },
              }}
              transition={SPRING_CARD}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                haptic.medium();
                sounds.swipe();
                onChoose(opt.key);
              }}
              className="relative flex items-center gap-3 p-4 text-left rounded-[22px] overflow-hidden"
              style={{
                background:
                  'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
                backdropFilter: 'blur(24px) saturate(180%)',
                WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                border: active
                  ? '1px solid rgba(234,196,96,0.5)'
                  : '1px solid rgba(255,255,255,0.10)',
                boxShadow: active
                  ? '0 10px 30px -8px rgba(234,196,96,0.35), inset 0 1px 0 rgba(255,255,255,0.18)'
                  : '0 8px 24px -12px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.12)',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <img
                src={opt.icon}
                alt=""
                className="w-12 h-12 object-contain"
                style={{ filter: 'drop-shadow(0 4px 14px rgba(140,180,240,0.4))' }}
              />
              <div className="flex-1">
                <p
                  className="font-semibold text-[15px]"
                  style={{ color: 'rgba(245,250,255,0.98)' }}
                >
                  {t(opt.titleKey)}
                </p>
                <p
                  className="text-[12px] leading-snug mt-0.5"
                  style={{ color: 'rgba(200,210,230,0.7)' }}
                >
                  {t(opt.subKey)}
                </p>
              </div>
            </motion.button>
          );
        })}
      </motion.div>
    </div>
  );
}
